import { useEffect, useState } from 'react';
import { sendRequest } from '../../../../services/request';
import { useLocation } from 'react-router-dom';
import { DataGrid, GridToolbar, esES } from '@mui/x-data-grid';
import { Box, Typography, Grid, Button, Modal, TextField } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ListHeader from './listHeader';
import dayjs from 'dayjs';
import ModalAsignarClienteEquipo from './modalAsignar';
import { DataGridStyle } from '../../../../styles/DataGridSytle';
import { CANALES } from '../../../../utils/utils';

const CampanaAsignarCliente = ({ pais }) => {
    const [rows, setRows] = useState();
    const [limit, setLimit] = useState(50);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(null);
    const [campañas, setCampañas] = useState([]);
    const [equipos, setEquipos] = useState([]);
    const URL = `${process.env.REACT_APP_API_URL}`;
    const location = useLocation();
    const clientes_donantes_string =
        location.state?.campana.division.nombre === 'Publicidad'
            ? 'Clientes'
            : 'Donantes';

    // Condicionales
    const [loading, setLoading] = useState(true);

    // Campos Search
    const [search, setSearch] = useState({
        campana: [location.state?.campana.id] || '',
        canal: '',
    });

    // Campos selected
    const [rowsSelected, setRowsSelected] = useState([]);

    const campana = location.state?.campana;

    // Modal
    const [open, setOpen] = useState(false);

    //Re-render
    const [guardado, setGuardado] = useState(false);

    useEffect(() => {
        setLoading(true);
        sendRequest(
            'get',
            `${URL}/${pais}/cliente/campana/asignados?limit=${limit}&page=${page}&searchCampana=${search.campana}`,
        ).then((res) => {
            setLoading(false);
            setRows(res.data.items);
            setRowCount(res.data.meta.totalItems);
        });
    }, [limit, page, guardado, URL, pais, search.campana]);

    useEffect(() => {
        sendRequest(
            'get',
            `${URL}/${pais}/campana?searchEstrategia=${search.canal}&vigente=false`,
        ).then((res) => setCampañas(res.data));
        sendRequest(
            'get',
            `${URL}/${pais}/equipos?campanas=${campana.id}`,
        ).then((res) => setEquipos(res.data.items));
    }, [URL, pais, campana.id, page, search.canal]);

    const columns = [
        {
            field: 'campana',
            headerName: 'Campaña',
            flex: 0.6,
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
            renderCell: ({ row }) => {
                const nombresCampana = new Set(
                    row.tarjetones?.map(
                        (tarjeton) =>
                            `${tarjeton.campana.nombre} - ${dayjs(
                                tarjeton.campana.vigencia,
                            ).format('YYYY')}`,
                    ),
                );
                return [...nombresCampana].toString();
            },
        },
        {
            field: 'nombres',
            headerName: 'Nombres',
            flex: 0.5,
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'apellidos',
            headerName: 'Apellidos',
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'tipo_documento',
            headerName: 'Tipo Doc.',
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
            align: 'center',
            valueGetter: ({ row }) => {
                return row.tipo_documento?.tipo;
            },
        },
        {
            field: 'numero_documento',
            headerName: 'Núm. Doc.',
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'genero',
            headerName: 'Género',
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
            valueGetter: ({ row }) => {
                return row.genero?.genero;
            },
        },
        {
            field: 'celular',
            headerName: 'Celular',
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'correo',
            headerName: 'Correo',
            flex: 1,
            headerClassName: 'header',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'tratamiento_datos',
            headerName: 'Trat. Datos',
            headerClassName: 'header',
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                if (params.value) {
                    return <CheckIcon></CheckIcon>;
                } else {
                    return <ClearIcon></ClearIcon>;
                }
            },
        },
    ];

    const handlePaginationChange = (event) => {
        setPage(event.page);
        setLimit(event.pageSize);
    };

    const handleOpenAsignarModal = () => {
        if (rowsSelected.length > 0) {
            setOpen(!open);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleAsignar = async (equipo) => {
        await sendRequest('post', `${URL}/${pais}/clientes-asignados`, {
            clientes: rowsSelected,
            campana: campana.id,
            equipo,
            cliente_equipo_current_week: dayjs(new Date()).week(),
        });
        setGuardado(!guardado);
        handleClose();
    };

    // Selecciona datos en el datagrid desde un input.
    const handleOnChangeDatosSeleccionar = async (event) => {
        const value = event.target.value;
        const numberOfRowsToSelect = parseInt(value, 10);

        if (!isNaN(numberOfRowsToSelect)) {
            const rowsToSelect = [];
            let currentPage = page;
            let remainingRows = numberOfRowsToSelect;

            while (remainingRows > 0) {
                setLoading(true);
                const res = await sendRequest(
                    'get',
                    `${URL}/${pais}/cliente/campana/asignados?limit=${limit}&page=${currentPage}&searchCampana=${search.campana}`,
                );
                setLoading(false);
                const rowsFromPage = res.data.items.slice(0, remainingRows);
                rowsToSelect.push(...rowsFromPage.map((row) => row.id));

                remainingRows -= rowsFromPage.length;
                currentPage += 1;

                if (currentPage * limit >= rowCount) break;
            }

            setRowsSelected(rowsToSelect);
        } else {
            setRowsSelected([]);
        }
    };

    return (
        <>
            <Box sx={{ height: '100%', width: '100%' }}>
                <Grid container spacing={5} py={3} pr={3}>
                    <Grid item xs={12} md={8}>
                        <Typography
                            variant="h4"
                            color="primary"
                            sx={{
                                textAlign: 'start',
                                marginLeft: '10px',
                            }}
                        >
                            Asignar {clientes_donantes_string} /{' '}
                            {campana?.nombre}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleOpenAsignarModal}
                        >
                            Asignar {clientes_donantes_string}
                        </Button>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item sx={12} md={6}>
                        <ListHeader
                            campañas={campañas}
                            canales={CANALES}
                            search={search}
                            setSearch={setSearch}
                        ></ListHeader>
                    </Grid>
                    <Grid item sx={12} md={4} mt={3}>
                        <TextField
                            label="Datos a seleccionar"
                            onChange={handleOnChangeDatosSeleccionar}
                        ></TextField>
                    </Grid>
                </Grid>
                <DataGrid
                    localeText={
                        esES.components.MuiDataGrid.defaultProps.localeText
                    }
                    disableColumnSelector
                    disableDensitySelector
                    disableColumnFilter
                    disableColumnMenu
                    keepNonExistentRowsSelected
                    checkboxSelection
                    loading={loading}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: limit },
                        },
                    }}
                    rows={rows ? rows : []}
                    columns={columns}
                    slots={{
                        toolbar: GridToolbar,
                    }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: false,
                            // Estos dos quitan el export
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    }}
                    paginationMode="server"
                    rowCount={rowCount ? rowCount : 0}
                    onPaginationModelChange={handlePaginationChange}
                    rowSelectionModel={rowsSelected}
                    onRowSelectionModelChange={(newRowSelection) => {
                        setRowsSelected(newRowSelection);
                    }}
                    getRowHeight={() => 'auto'}
                    sx={[
                        DataGridStyle,
                        {
                            '& .MuiDataGrid-virtualScroller': {
                                minHeight: '30vh',
                            },
                        },
                    ]}
                />
            </Box>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <ModalAsignarClienteEquipo
                    URL={URL}
                    pais={pais}
                    equipos={equipos}
                    handleClose={handleClose}
                    handleAsignar={handleAsignar}
                ></ModalAsignarClienteEquipo>
            </Modal>
        </>
    );
};

export default CampanaAsignarCliente;
